import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeToggle } from './ThemeToggle';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      // Update active section based on scroll position
      const sections = ['home', 'subsidiaries', 'about', 'contact'];
      for (const sectionId of sections) {
        const section = document.getElementById(sectionId);
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
      setActiveSection(sectionId);
    }
  };
  const menuItems = [{
    name: 'Home',
    id: 'home'
  }, {
    name: 'Our Companies',
    id: 'subsidiaries'
  }, {
    name: 'About Us',
    id: 'about'
  }, {
    name: 'Contact',
    id: 'contact'
  }];
  return <motion.header className={`sticky top-0 z-50 w-full bg-white dark:bg-gray-900 ${scrolled ? 'shadow-md' : 'shadow-sm'}`} initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} data-id="element-180">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between" data-id="element-181">
        <motion.div className="flex items-center" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} data-id="element-182">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/i5n6sWUDk3t4qAJHxk3L83/SOLO_GROUP.png" alt="Solochus Group Logo" className="h-10 md:h-12 w-auto" data-id="element-183" />
        </motion.div>
        {/* Desktop Navigation */}
        <motion.nav className="hidden md:flex items-center space-x-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-184">
          {menuItems.map((item, index) => <motion.div key={item.id} className="relative" initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.3 + index * 0.1
        }} data-id="element-185">
              <motion.div className={`absolute inset-0 bg-green-100 dark:bg-green-900/40 rounded-lg -z-10 ${activeSection === item.id ? 'opacity-100' : 'opacity-0'}`} initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: activeSection === item.id ? 1 : 0.8,
            opacity: activeSection === item.id ? 1 : 0
          }} transition={{
            duration: 0.3
          }} layoutId={`nav-background-${item.id}`} data-id="element-186" />
              <motion.a href={`#${item.id}`} className={`font-bold text-indigo-700 dark:text-indigo-300 relative px-4 py-2 block transition-colors ${activeSection === item.id ? 'text-indigo-800 dark:text-indigo-200' : 'hover:text-indigo-600 dark:hover:text-indigo-100'}`} onClick={e => scrollToSection(e, item.id)} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-187">
                {item.name}
                <motion.div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-green-500 ${activeSection === item.id ? 'opacity-100' : 'opacity-0'}`} initial={{
              scaleX: 0
            }} animate={{
              scaleX: activeSection === item.id ? 1 : 0,
              opacity: activeSection === item.id ? 1 : 0
            }} transition={{
              duration: 0.3
            }} data-id="element-188" />
              </motion.a>
            </motion.div>)}
          <ThemeToggle data-id="element-189" />
        </motion.nav>
        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center space-x-4" data-id="element-190">
          <ThemeToggle data-id="element-191" />
          <motion.button className="p-2 rounded-md focus:outline-none" onClick={() => setIsMenuOpen(!isMenuOpen)} whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} data-id="element-192">
            {isMenuOpen ? <X size={24} data-id="element-193" /> : <Menu size={24} data-id="element-194" />}
          </motion.button>
        </div>
      </div>
      {/* Mobile Navigation */}
      <AnimatePresence data-id="element-195">
        {isMenuOpen && <motion.div className="md:hidden bg-white dark:bg-gray-900 pb-4 px-4" initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-196">
            <nav className="flex flex-col space-y-4" data-id="element-197">
              {menuItems.map((item, index) => <motion.div key={item.id} className="relative" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.1,
            duration: 0.3
          }} data-id="element-198">
                  <motion.div className={`absolute inset-0 bg-green-100 dark:bg-green-900/40 rounded-md -z-10 ${activeSection === item.id ? 'opacity-100' : 'opacity-0'}`} initial={{
              opacity: 0
            }} animate={{
              opacity: activeSection === item.id ? 1 : 0
            }} transition={{
              duration: 0.3
            }} data-id="element-199" />
                  <motion.a href={`#${item.id}`} className={`font-bold px-4 py-3 block rounded-md ${activeSection === item.id ? 'text-indigo-800 dark:text-indigo-200' : 'text-indigo-700 dark:text-indigo-300 hover:text-indigo-600 dark:hover:text-indigo-100'}`} onClick={e => scrollToSection(e, item.id)} whileHover={{
              x: 5,
              backgroundColor: 'rgba(220, 252, 231, 0.5)' // light green
            }} transition={{
              duration: 0.2
            }} data-id="element-200">
                    {item.name}
                  </motion.a>
                </motion.div>)}
            </nav>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}